import React, { useMemo, type CSSProperties } from 'react';
import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;

type DayjsRange = [Dayjs, Dayjs];
type RangeValueInput = [Dayjs | string, Dayjs | string] | null | undefined;

interface FullDayRangePickerProps {
  value?: RangeValueInput;
  onChange?: (value: DayjsRange | null) => void;
  style?: CSSProperties;
  [key: string]: unknown;
}

/**
 * FullDayRangePicker (Granularity: Day)
 *
 * 使用場景：
 * 當使用者只需選擇「日期」，但業務邏輯需要涵蓋該日期的「完整時間範圍」時使用。
 *
 * 行為特徵：
 * 1. UI 顯示：只顯示日期選擇器，不顯示時分秒。
 * 2. 輸入/輸出：React 層面為 Dayjs 物件。
 * 3. 資料提交：
 *    - 在 Form 層 (Content.jsx) 會將 Dayjs 物件傳給 setQuery。
 *    - 在 API Service 層 (apiService.js) 會自動偵測 Dayjs 並轉為 ISO 8601 String。
 * 4. 邏輯：在 onChange 時，強制將 Start 時間設為 00:00:00，End 時間設為 23:59:59。
 */
const FullDayRangePicker: React.FC<FullDayRangePickerProps> = ({ value, onChange, style, ...props }) => {
  const { t } = useTranslation();

  // 確保 value 是 dayjs 格式
  const dateValue = useMemo<DayjsRange | null>(() => {
    if (!value || value.length !== 2) return null;
    return [
      typeof value[0] === 'string' ? dayjs(value[0]) : value[0],
      typeof value[1] === 'string' ? dayjs(value[1]) : value[1]
    ];
  }, [value]);

  const handleChange = (dates: unknown) => {
    if (!Array.isArray(dates) || dates.length !== 2 || !dates[0] || !dates[1]) {
      onChange?.(null);
      return;
    }

    // 強制設定為一天的開始與結束
    const start = (dates[0] as Dayjs).startOf('day');
    const end = (dates[1] as Dayjs).endOf('day');

    // 更新 React State (Dayjs)
    onChange?.([start, end]);
  };

  // 移除 showTime prop，讓 UI 只顯示日期選擇器
  return (
    <RangePicker
      {...(props as Record<string, unknown>)}
      value={dateValue as never}
      onChange={handleChange as never}
      placeholder={[
        t('common.table.filters.date_range_start_placeholder'),
        t('common.table.filters.date_range_end_placeholder')
      ]}
      // 不顯示時分秒選擇器
      style={{ width: '100%', ...style }}
    />
  );
};

export default FullDayRangePicker;
